/**
 * A price book as a matrix of effective prices (owner, 2026-09-29): rows are TLDs (or, on a TLD
 * page, price books), columns the operations, each cell what the customer pays for one year —
 * priced by the engine (`GET /commerce/pricing/books/{id}/matrix`), with where it comes from
 * (the TLD's own rule or the book's default). A cell opens one dialog for the TLD in that book
 * with every operation as a row ("nhập giá 1 lần"), the clicked one focused; the rows changed are
 * saved together (`POST /commerce/pricing/books/{id}/tld-prices`). Rule technicalities (priority,
 * limits, versions, windows) stay in "History & rule details".
 */
import { MoreHorizontal, Plus, Search } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { MatrixCell, MatrixRow, PriceBook, PriceMatrix, PriceRule } from '../../api/areas/commerce';
import type { TldPricesInput } from '../../api/areas/operator';
import { Badge } from '../../components/Badge';
import { Callout, Field } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatMoney } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonField, ReasonForm, selectClass, type DialogBodyProps } from '../operator/shared';
import { decimalInput, GRID_DIALOG, GridCell, GridHeader, GridRow, isDecimal, roundMoney, rowErrors } from './PriceGrid';
import { Amount } from './shared';

export const MATRIX_OPS = ['REGISTER', 'RENEW', 'TRANSFER', 'RESTORE'] as const;
type Lookup = (key: string, options?: Record<string, unknown>) => string;

/** "29/09/2026" in the interface language. */
export function shortDate(iso: string, lang: string): string {
  return new Date(iso).toLocaleDateString(lang.startsWith('vi') ? 'vi-VN' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** A rule in words: "Giá vốn + 25 %", "450.000 ₫", with its limits. */
export function useRuleWords() {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  return (
    r: {
      rule_type?: PriceRule['rule_type'];
      type?: PriceRule['rule_type'];
      value: string;
      min_amount?: string | null;
      max_amount?: string | null;
    },
    currency: string,
  ) => {
    const kind = r.rule_type ?? r.type ?? 'FIXED_PRICE';
    const value = kind === 'MARKUP_PERCENT' ? r.value : formatMoney({ amount: r.value, currency });
    const limits = [
      r.min_amount
        ? tr('matrix.ruleMin', {
            value: formatMoney({ amount: r.min_amount, currency }),
          })
        : null,
      r.max_amount
        ? tr('matrix.ruleMax', {
            value: formatMoney({ amount: r.max_amount, currency }),
          })
        : null,
    ].filter(Boolean);
    return `${tr(`matrix.rule.${kind}`, { value })}${limits.length ? ` (${limits.join(', ')})` : ''}`;
  };
}

const SOURCE_TONE: Record<MatrixCell['source'], 'ok' | 'info' | 'neutral' | 'warn' | 'crit'> = {
  SPECIFIC: 'info',
  DEFAULT: 'neutral',
  FREE: 'ok',
  NO_COST: 'warn',
  NO_RULE: 'crit',
  NOT_OFFERED: 'neutral',
  NO_PRICE: 'crit',
};

/** One cell: the price and a small marker of where it comes from. */
function CellContent({ cell, currency }: { cell: MatrixCell; currency: string }) {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      {cell.amount !== null ? <Amount amount={cell.amount} currency={currency} className="font-medium" /> : <span className="text-muted-foreground">—</span>}
      <Badge tone={SOURCE_TONE[cell.source]} icon={false} className="px-1.5 py-0 text-[11px]" title={cell.error ?? undefined}>
        {tr(`matrix.source.${cell.source}`)}
        {cell.basis === 'RENEWAL' ? ` · ${t('matrix.renewalBasis')}` : ''}
      </Badge>
    </span>
  );
}

/** What the TLD price dialog edits: one book, one TLD, every operation (`focus`: the cell clicked). */
export interface CellTarget {
  book: Pick<PriceBook, 'id' | 'code' | 'currency'>;
  tldId: string;
  zone: string;
  cells: MatrixCell[];
  defaults: PriceMatrix['defaults'];
  focus?: MatrixCell['operation'];
}

type Choice = 'KEEP' | 'DEFAULT' | PriceRule['rule_type'];
const CHOICES: Choice[] = ['KEEP', 'DEFAULT', 'FIXED_PRICE', 'MARKUP_PERCENT', 'MARKUP_FIXED'];
interface Setting {
  choice: Choice;
  value: string;
}

interface PricedRule {
  type: PriceRule['rule_type'];
  value: string;
  years: number | null;
  min_amount?: string | null;
  max_amount?: string | null;
}

/** A rule over the TLD's registry cost (book currency, FX applied), as the engine prices it. */
export function priceOf(rule: PricedRule, cost: string | null, years: number): number | null {
  const v = Number(rule.value);
  const perYear = rule.years === null ? Math.max(years, 1) : 1;
  const c = cost === null ? null : Number(cost);
  let p: number;
  if (rule.type === 'FIXED_PRICE') p = v * perYear;
  else if (c === null) return null;
  else if (rule.type === 'MARKUP_FIXED') p = c + v * perYear;
  else p = c + (c * v) / 100;
  if (rule.min_amount) p = Math.max(p, Number(rule.min_amount) * perYear);
  if (rule.max_amount) p = Math.min(p, Number(rule.max_amount) * perYear);
  return p;
}

const defaultFor = (defaults: PriceMatrix['defaults'], op: string) => defaults.find((d) => d.operation === op && d.years === null) ?? defaults.find((d) => d.operation === op);
const editable = (c: MatrixCell) => c.source !== 'NOT_OFFERED' && c.basis !== 'FREE';
const hasOwn = (c: MatrixCell) => c.source === 'SPECIFIC' && c.rule !== null;

const GRID = 'sm:grid-cols-[6.5rem_minmax(0,1.4fr)_minmax(0,1.1fr)_7rem_minmax(0,7.5rem)]';

/** "Giá bán .com.vn · DEMO_RETAIL_STD": every operation of the TLD in one grid, saved together. */
export function TldPriceForm({ close, changed, target }: DialogBodyProps & { target: CellTarget }) {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  const words = useRuleWords();
  const { book, defaults } = target;
  const cur = book.currency;
  const rows = MATRIX_OPS.flatMap((op) => target.cells.filter((c) => c.operation === op));
  const [settings, setSettings] = useState<Record<string, Setting>>(() => Object.fromEntries(rows.map((c) => [c.operation, { choice: 'KEEP', value: '' }])));
  const [all, setAll] = useState<Setting>({ choice: 'FIXED_PRICE', value: '' });
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const [sent, setSent] = useState<string[]>([]);
  const focusRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    focusRef.current?.focus();
    focusRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);
  const w = useWrite((body: TldPricesInput, opts) => api.publishTldPrices(book.id, body, opts));
  const done = w.state.phase === 'done';
  const allowed = (c: MatrixCell, ch: Choice) => ch !== 'DEFAULT' || hasOwn(c);
  const set = (op: string, s: Partial<Setting>) => setSettings((x) => ({ ...x, [op]: { ...(x[op] ?? { choice: 'KEEP', value: '' }), ...s } }));
  const pending = rows.filter((c) => editable(c) && settings[c.operation]?.choice !== 'KEEP');
  const valid = (s: Setting | undefined) => !s || s.choice === 'KEEP' || s.choice === 'DEFAULT' || isDecimal(s.value);
  const ready = Boolean(reason.trim()) && pending.length > 0 && pending.every((c) => valid(settings[c.operation]));
  const errors = w.state.phase === 'error' ? rowErrors(w.state.error, sent) : {};
  const choiceLabel: Record<Choice, string> = {
    KEEP: t('matrix.grid.keep'),
    DEFAULT: t('matrix.cell.useDefault'),
    FIXED_PRICE: t('matrix.cell.fixed'),
    MARKUP_PERCENT: t('matrix.cell.percent'),
    MARKUP_FIXED: t('matrix.cell.markup'),
  };
  const unit = (ch: Choice) => (ch === 'MARKUP_PERCENT' ? '%' : cur);

  const how = (c: MatrixCell): ReactNode =>
    c.source === 'NO_COST'
      ? t('matrix.cell.noCost')
      : c.source === 'NOT_OFFERED'
        ? t('matrix.cell.notOffered')
        : c.source === 'FREE'
          ? t('matrix.cell.free')
          : c.rule
            ? `${words(c.rule, cur)} — ${hasOwn(c) ? t('matrix.cell.fromSpecific') : t('matrix.cell.fromDefault')}`
            : t('matrix.cell.noRule');

  const preview = (c: MatrixCell): ReactNode => {
    const s = settings[c.operation];
    if (!s || s.choice === 'KEEP' || !editable(c)) return <span className="text-muted-foreground">—</span>;
    if (c.basis === 'RENEWAL') return <span className="text-xs text-muted-foreground">{t('matrix.grid.renewal')}</span>;
    let p: number | null;
    if (s.choice === 'DEFAULT') {
      const d = defaultFor(defaults, c.operation);
      if (!d) return <span className="text-xs text-warning">{t('matrix.grid.noDefault')}</span>;
      p = priceOf({ type: d.rule_type, value: d.value, years: d.years, min_amount: d.min_amount, max_amount: d.max_amount }, c.registry_cost, c.years);
    } else {
      if (!isDecimal(s.value)) return <span className="text-muted-foreground">—</span>;
      p = priceOf({ type: s.choice, value: s.value, years: hasOwn(c) && c.rule ? c.rule.years : null }, c.registry_cost, c.years);
    }
    if (p === null) return <span className="text-xs text-warning">{t('matrix.grid.noCost')}</span>;
    return (
      <span className="inline-flex flex-col items-end">
        <Amount amount={roundMoney(p, cur)} currency={cur} className="font-semibold" />
        {c.basis === 'OWN_PRICE_PLUS_RENEWAL' ? <span className="text-[11px] text-muted-foreground">{t('matrix.grid.plusRenewal')}</span> : null}
      </span>
    );
  };

  const submit = () => {
    const items: TldPricesInput['items'] = pending.map((c) => {
      const s = settings[c.operation] ?? { choice: 'KEEP', value: '' };
      return s.choice === 'DEFAULT'
        ? { operation: c.operation, action: 'USE_DEFAULT' }
        : {
            operation: c.operation,
            action: 'SET',
            rule_type: s.choice as PriceRule['rule_type'],
            value: s.value.trim(),
            // Same scope as the TLD's rule in force (a rule for a number of years stays one).
            years: hasOwn(c) && c.rule ? c.rule.years : null,
          };
    });
    setSent(items.map((i) => i.operation));
    void w
      .run({ tld_id: target.tldId, items, effective_from: from ? new Date(from).toISOString() : undefined, reason: reason.trim() })
      .then((r) => r && changed());
  };

  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {done ? (
        <Callout tone="ok">{t('matrix.grid.saved', { count: sent.length })}</Callout>
      ) : (
        <>
          <div className="grid gap-2 rounded-lg border bg-muted/30 p-3" role="group" aria-label={t('matrix.grid.all')}>
            <span className="text-xs font-medium text-muted-foreground">{t('matrix.grid.all')}</span>
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-full sm:w-56">
                <select className={selectClass} value={all.choice} onChange={(e) => setAll((x) => ({ ...x, choice: e.target.value as Choice }))} aria-label={t('matrix.grid.method')}>
                  {CHOICES.map((c) => (
                    <option key={c} value={c}>
                      {choiceLabel[c]}
                    </option>
                  ))}
                </select>
              </div>
              {all.choice !== 'KEEP' && all.choice !== 'DEFAULT' ? (
                <Input inputMode="decimal" value={all.value} onChange={(e) => setAll((x) => ({ ...x, value: decimalInput(e.target.value) }))} placeholder={unit(all.choice)} aria-label={`${t('matrix.cell.value')} (${unit(all.choice)})`} className="w-32" />
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  setSettings((x) => {
                    const next = { ...x };
                    for (const c of rows) if (editable(c) && allowed(c, all.choice)) next[c.operation] = { choice: all.choice, value: all.value };
                    return next;
                  })
                }
              >
                {t('matrix.grid.apply')}
              </Button>
            </div>
          </div>
          <div className="grid gap-2 sm:gap-0">
            <GridHeader template={GRID} cols={[t('matrix.grid.op'), t('matrix.grid.current'), t('matrix.grid.method'), t('matrix.cell.value'), t('matrix.grid.preview')]} />
            {rows.map((c) => {
              const s = settings[c.operation] ?? { choice: 'KEEP', value: '' };
              const op = tr(`matrix.op.${c.operation}`);
              const focus = c.operation === target.focus;
              return (
                <GridRow key={c.operation} template={GRID} focus={focus} error={errors[c.operation]} label={op}>
                  <span className="font-medium">{op}</span>
                  <GridCell label={t('matrix.grid.current')}>
                    <span className="flex flex-wrap items-baseline gap-x-2">
                      {c.amount !== null ? <Amount amount={c.amount} currency={cur} className="font-medium" /> : <span className="text-muted-foreground">—</span>}
                      <Badge tone={SOURCE_TONE[c.source]} icon={false} className="px-1.5 py-0 text-[11px]">
                        {tr(`matrix.source.${c.source}`)}
                      </Badge>
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {how(c)}
                      {c.registry_cost !== null ? ` · ${t('matrix.grid.costLine', { cost: formatMoney({ amount: c.registry_cost, currency: cur }) })}` : ''}
                    </span>
                  </GridCell>
                  <GridCell label={t('matrix.grid.method')}>
                    <select
                      ref={focus ? focusRef : undefined}
                      className={selectClass}
                      value={s.choice}
                      disabled={!editable(c)}
                      onChange={(e) => set(c.operation, { choice: e.target.value as Choice })}
                      aria-label={`${op} · ${t('matrix.grid.method')}`}
                    >
                      {CHOICES.filter((ch) => allowed(c, ch)).map((ch) => (
                        <option key={ch} value={ch}>
                          {choiceLabel[ch]}
                        </option>
                      ))}
                    </select>
                  </GridCell>
                  <GridCell label={`${t('matrix.cell.value')} (${unit(s.choice)})`}>
                    {s.choice !== 'KEEP' && s.choice !== 'DEFAULT' ? (
                      <Input
                        inputMode="decimal"
                        value={s.value}
                        onChange={(e) => set(c.operation, { value: decimalInput(e.target.value) })}
                        placeholder={unit(s.choice)}
                        aria-label={`${op} · ${t('matrix.cell.value')} (${unit(s.choice)})`}
                        aria-invalid={s.value !== '' && !isDecimal(s.value)}
                        required
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </GridCell>
                  <GridCell label={t('matrix.grid.preview')} className="sm:text-right">
                    {preview(c)}
                  </GridCell>
                </GridRow>
              );
            })}
          </div>
          <div className="grid gap-3 sm:grid-cols-[auto_1fr] sm:items-start">
            <Field label={t('matrix.cell.from')} hint={t('matrix.cell.fromHint')}>
              <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} className="w-60" />
            </Field>
            <div className="grid gap-3">
              <ReasonField value={reason} onChange={setReason} />
            </div>
          </div>
          {pending.length === 0 ? <p className="text-xs text-muted-foreground">{t('matrix.grid.nothing')}</p> : null}
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!ready} submit={done ? undefined : pending.length ? t('matrix.grid.save', { count: pending.length }) : t('matrix.cell.save')} onClose={close} />
    </form>
  );
}

/** The controlled TLD price dialog. */
function CellDialog({ target, onClose, onChanged }: { target: CellTarget | null; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('commerce');
  return (
    <ActionDialog
      label=""
      trigger={false}
      open={target !== null}
      onOpenChange={(o) => !o && onClose()}
      wide={GRID_DIALOG}
      title={target ? t('matrix.grid.title', { zone: target.zone, book: target.book.code }) : ''}
      description={t('matrix.grid.description')}
      onChanged={onChanged}
    >
      {(p) => (target ? <TldPriceForm {...p} target={target} /> : null)}
    </ActionDialog>
  );
}

/** "Quy tắc mặc định": the book's rule for every TLD, per operation, in words. */
function DefaultsLine({ matrix }: { matrix: PriceMatrix }) {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  const words = useRuleWords();
  if (!matrix.defaults.length) return <Callout tone="warn">{t('matrix.defaultsNone')}</Callout>;
  return (
    <p className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
      <span className="font-medium">{t('matrix.defaults')}:</span>
      {MATRIX_OPS.map((op) => {
        const d = defaultFor(matrix.defaults, op);
        return (
          <span key={op}>
            <span className="text-muted-foreground">{tr(`matrix.op.${op}`)}</span> {d ? words(d, matrix.currency) : '—'}
          </span>
        );
      })}
    </p>
  );
}

/** A table of cells: `rows` × the operations, each cell opening the dialog at its operation. */
function CellTable({
  first,
  rows,
  open,
}: {
  first: string;
  rows: {
    key: string;
    label: ReactNode;
    cells: MatrixCell[];
    currency: string;
    target: (focus: MatrixCell['operation']) => CellTarget;
  }[];
  open: (t: CellTarget) => void;
}) {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <th className="px-4 py-2 text-left">
              {first} <span className="font-normal normal-case">· {t('matrix.oneYear')}</span>
            </th>
            {MATRIX_OPS.map((op) => (
              <th key={op} className="px-3 py-2 text-right">
                {tr(`matrix.op.${op}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b last:border-b-0">
              <td className="px-4 py-2 align-top font-medium">{r.label}</td>
              {MATRIX_OPS.map((op) => {
                const c = r.cells.find((x) => x.operation === op);
                return (
                  <td key={op} className="px-1 py-1 text-right align-top">
                    {c ? (
                      <button
                        type="button"
                        className="w-full rounded-md px-2 py-1 text-right hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                        onClick={() => open(r.target(op))}
                        aria-label={`${r.key} · ${tr(`matrix.op.${op}`)}`}
                      >
                        <CellContent cell={c} currency={r.currency} />
                      </button>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One price book's matrix: default rule line, search / filter, TLD rows. */
export function BookMatrix({ book, refreshKey, onChanged }: { book: PriceBook; refreshKey?: number; onChanged?: () => void }) {
  const { t } = useTranslation('commerce');
  const [tick, setTick] = useState(0);
  const result = useApi((signal) => api.getPriceBookMatrix(book.id, {}, { signal }), [book.id, refreshKey, tick]);
  const [q, setQ] = useState('');
  const [onlySpecific, setOnlySpecific] = useState(false);
  const [target, setTarget] = useState<CellTarget | null>(null);
  if (result.status === 'loading') return <Loading rows={3} />;
  if (result.status === 'error') return <DataError error={result.error} title={t('matrix.loadError')} onRetry={result.reload} />;
  const m = result.data;
  const needle = q.trim().toLowerCase().replace(/^\./, '');
  const rows = m.rows.filter((r) => (!needle || r.zone.includes(needle)) && (!onlySpecific || r.cells.some((c) => c.source === 'SPECIFIC')));
  return (
    <div className="grid min-w-0 gap-3">
      <div className="px-5">
        <DefaultsLine matrix={m} />
      </div>
      <div className="flex flex-wrap items-center gap-3 px-5">
        <div className="relative">
          <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-muted-foreground" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('matrix.search')} aria-label={t('matrix.search')} className="h-9 w-48 pl-8" />
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" className="size-4" checked={onlySpecific} onChange={(e) => setOnlySpecific(e.target.checked)} />
          {t('matrix.onlySpecific')}
        </label>
      </div>
      {rows.length ? (
        <CellTable
          first={t('matrix.tld')}
          rows={rows.map((r: MatrixRow) => ({
            key: r.zone,
            label: `.${r.zone}`,
            cells: r.cells,
            currency: m.currency,
            target: (focus) => ({ book, tldId: r.tld_id, zone: r.zone, cells: r.cells, defaults: m.defaults, focus }),
          }))}
          open={setTarget}
        />
      ) : (
        <p className="px-5 pb-4 text-sm text-muted-foreground">{t('matrix.empty')}</p>
      )}
      <p className="px-5 pb-4 text-xs text-muted-foreground">{t('matrix.note')}</p>
      <CellDialog
        target={target}
        onClose={() => setTarget(null)}
        onChanged={() => {
          setTick((n) => n + 1);
          onChanged?.();
        }}
      />
    </div>
  );
}

/** A TLD's selling prices: one row per price book (the TLD page). */
export function TldBookMatrix({ tldId, zone, books, refreshKey }: { tldId: string; zone: string; books: PriceBook[]; refreshKey?: number }) {
  const { t } = useTranslation('commerce');
  const [tick, setTick] = useState(0);
  const usable = books.filter((b) => b.status !== 'RETIRED');
  const ids = usable.map((b) => b.id).join(',');
  const result = useApi((signal) => Promise.all(usable.map((b) => api.getPriceBookMatrix(b.id, { tld_id: tldId }, { signal }))), [tldId, ids, refreshKey, tick]);
  const [target, setTarget] = useState<CellTarget | null>(null);
  if (result.status === 'loading') return <Loading rows={2} />;
  if (result.status === 'error') return <DataError error={result.error} title={t('matrix.loadError')} onRetry={result.reload} />;
  const rows = result.data.flatMap((m) => {
    const book = usable.find((b) => b.id === m.price_book_id);
    const row = m.rows[0];
    if (!book || !row) return [];
    return [
      {
        key: book.code,
        label: <span className="font-mono text-xs">{book.code}</span>,
        cells: row.cells,
        currency: m.currency,
        target: (focus: MatrixCell['operation']) => ({ book, tldId, zone, cells: row.cells, defaults: m.defaults, focus }),
      },
    ];
  });
  return (
    <div className="grid min-w-0 gap-2">
      {rows.length ? <CellTable first={t('matrix.book')} rows={rows} open={setTarget} /> : <p className="px-5 pb-4 text-sm text-muted-foreground">{t('matrix.empty')}</p>}
      <p className="px-5 pb-4 text-xs text-muted-foreground">{t('matrix.note')}</p>
      <CellDialog target={target} onClose={() => setTarget(null)} onChanged={() => setTick((n) => n + 1)} />
    </div>
  );
}

/** "Thêm giá riêng cho TLD…": the TLD picker first, then that TLD's grid. */
function PickThenGrid({ close, changed, book }: DialogBodyProps & { book: PriceBook }) {
  const { t } = useTranslation('commerce');
  const registries = useApi((signal) => api.listRegistries({ signal }), []);
  const zones =
    registries.status === 'success'
      ? registries.data.items
          .flatMap((r) => r.tlds)
          .filter((z, i, all) => all.findIndex((x) => x.id === z.id) === i)
          .sort((a, b) => a.zone.localeCompare(b.zone))
      : [];
  const [tld, setTld] = useState('');
  const matrix = useApi((signal) => (tld ? api.getPriceBookMatrix(book.id, { tld_id: tld }, { signal }) : Promise.resolve(null)), [book.id, tld]);
  const m = matrix.status === 'success' ? matrix.data : null;
  const row = m?.rows[0];
  return (
    <div className="grid gap-4">
      <Field label={t('matrix.pickTld')}>
        <select className={`${selectClass} sm:max-w-xs`} value={tld} onChange={(e) => setTld(e.target.value)}>
          <option value="">{t('matrix.choose')}</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              .{z.zone}
            </option>
          ))}
        </select>
      </Field>
      {tld && matrix.status === 'loading' ? <Loading rows={2} /> : null}
      {matrix.status === 'error' ? <DataError error={matrix.error} title={t('matrix.loadError')} onRetry={matrix.reload} /> : null}
      {m && row ? (
        <TldPriceForm key={tld} close={close} changed={changed} target={{ book, tldId: row.tld_id, zone: row.zone, cells: row.cells, defaults: m.defaults }} />
      ) : (
        <>
          {!tld ? <p className="text-sm text-muted-foreground">{t('matrix.grid.pickFirst')}</p> : null}
          <FormFooter busy={false} onClose={close} />
        </>
      )}
    </div>
  );
}

export function AddSpecificPriceButton({ book, onChanged }: { book: PriceBook; onChanged: () => void }) {
  const { t } = useTranslation('commerce');
  if (book.status === 'RETIRED') return null;
  return (
    <ActionDialog label={t('matrix.addSpecific')} icon={Plus} title={t('matrix.pick', { book: book.code })} description={t('matrix.grid.description')} wide={GRID_DIALOG} onChanged={onChanged}>
      {(p) => <PickThenGrid {...p} book={book} />}
    </ActionDialog>
  );
}

/** "…": status changes of a book, each confirmed with a reason. */
export function BookMenu({ book, onChanged }: { book: PriceBook; onChanged: () => void }) {
  const { t } = useTranslation('commerce');
  const { t: to } = useTranslation('operator');
  const [open, setOpen] = useState<PriceBook['status'] | null>(null);
  if (book.status === 'RETIRED') return null;
  const run = (status: PriceBook['status']) => (reason: string, opts: Parameters<typeof api.setPriceBookStatus>[2]) => api.setPriceBookStatus(book.id, { status, reason }, opts);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" size="sm" variant="ghost" aria-label={t('matrix.more')} title={t('matrix.more')}>
            <MoreHorizontal className="size-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {book.status === 'DRAFT' ? <DropdownMenuItem onSelect={() => setOpen('ACTIVE')}>{to('pricing.activate')}</DropdownMenuItem> : null}
          <DropdownMenuItem variant="destructive" onSelect={() => setOpen('RETIRED')}>
            {to('pricing.retire')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ActionDialog label="" trigger={false} open={open !== null} onOpenChange={(o) => !o && setOpen(null)} title={`${to('pricing.statusTitle')} · ${book.code}`} onChanged={onChanged}>
        {(p) =>
          open ? (
            <ReasonForm
              {...p}
              warning={open === 'RETIRED' ? to('pricing.retiredForever') : undefined}
              submit={open === 'RETIRED' ? to('pricing.retire') : to('pricing.activate')}
              danger={open === 'RETIRED'}
              run={run(open)}
              done={() => (open === 'RETIRED' ? to('pricing.statusRetired') : to('pricing.statusActive'))}
            />
          ) : null
        }
      </ActionDialog>
    </>
  );
}
